import { Icon } from "@material-ui/core";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  OrientationType,
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import BottomModal from "components/bottomModal/BottomModal";
import { Composition as IComposition } from "domains/catalog/aggregates/Composition";
import { Product } from "domains/catalog/aggregates/Product";
import { SeccionedProduct } from "domains/catalog/aggregates/SeccionedProduct";
import { OrderComposition } from "domains/order/agreggates/order/OrderComposition";
import { OrderCompositionItem } from "domains/order/agreggates/order/OrderCompositionItem";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { hexToRGBA, moneyMask } from "utils/utils";
import { ReactComponent as Crown } from "../../../../assets/icons/crown.svg";
import { Composition } from "../composition/Composition";
import styles from "./CompositionList.module.scss";
import { Keyboard } from "components/keyboard/Keyboard";
import { useTranslation } from "i18n/useTranslation";

export enum StepType {
  ADDITIONAL = 1,
  GEN_OBS = 2,
  SUGGESTIONS = 3,
}

export interface IStep {
  id: string;
  text: string;
  checked: boolean;
  enable?: boolean;
  number?: number;
  isGeneralObservation?: boolean;
  type: StepType;
}

interface CompositionProps {
  compositions: IComposition[];
  compostionFreeMax: number | undefined;
  onFinish: (orderCompositions: OrderComposition[], minimos: boolean) => void;
  setSelectedItemsFree: React.Dispatch<
    React.SetStateAction<OrderCompositionItem[]>
  >;
  selectedItemsFree: OrderCompositionItem[];
  getTotalItemsFree: () => number;
  selectedItemsReplica: OrderCompositionItem[];
  setSelectedItemsReplica: React.Dispatch<
    React.SetStateAction<OrderCompositionItem[]>
  >;
  addToCard: VoidFunction;
  productBaseValue: number;
  generalObservation?: boolean;
  observation: string;
  handleChangeObservation: (observation: string) => void;
  productSuggestions: string[];
}

export const CompositionList: FC<CompositionProps> = ({
  compositions,
  onFinish,
  compostionFreeMax,
  setSelectedItemsFree,
  selectedItemsFree,
  getTotalItemsFree,
  selectedItemsReplica,
  setSelectedItemsReplica,
  addToCard,
  productBaseValue,
  generalObservation,
  observation,
  handleChangeObservation,
  productSuggestions,
}) => {
  const { kiosk } = useKiosk();
  const { resolution, orientation } = useScreenStore();
  const { storeId } = useParams<{ storeId: string }>();
  const { catalog } = useCatalogStore();
  const { openProductDetail } = useCatalogStore();
  const { t } = useTranslation();
  const [addProductCart, getProductQuantity, removeCartItemByProductId] =
    useCartStore((state: ICartContext) => [
      state.addProductCart,
      state.getProductQuantity,
      state.removeCartItemByProductId,
    ]);

  const suggestionProducts = useMemo(() => {
    const store = catalog?.stores[parseInt(storeId)];

    let sugProducts: Product[] = [];
    if (store) {
      store.seccionedProduct.forEach((it: SeccionedProduct) => {
        it.products.forEach((p: Product) => {
          if (productSuggestions.includes(p.id)) {
            sugProducts.push(p);
          }
        });
      });
    }
    return sugProducts;
  }, [catalog, productSuggestions, storeId]);

  let startCompostion: IStep[] =
    !generalObservation && productSuggestions.length === 0
      ? compositions.map((item) => ({
          id: item.id,
          text: item.description,
          checked: item.min === 0,
          enable: false,
          type: StepType.ADDITIONAL,
        }))
      : generalObservation && productSuggestions.length === 0
      ? [
          ...compositions.map((item) => ({
            id: item.id,
            text: item.description,
            checked: item.min === 0,
            enable: false,
            type: StepType.ADDITIONAL,
          })),
          {
            id: "",
            text: "Observação",
            checked: true,
            enable: false,
            type: StepType.GEN_OBS,
          },
        ]
      : !generalObservation && productSuggestions.length > 0
      ? [
          ...compositions.map((item) => ({
            id: item.id,
            text: item.description,
            checked: item.min === 0,
            enable: false,
            type: StepType.ADDITIONAL,
          })),
          {
            id: "",
            text: "Sugestões",
            checked: true,
            enable: false,
            type: StepType.SUGGESTIONS,
          },
        ]
      : [
          ...compositions.map((item) => ({
            id: item.id,
            text: item.description,
            checked: item.min === 0,
            enable: false,
            type: StepType.ADDITIONAL,
          })),
          {
            id: "",
            text: "Observação",
            checked: true,
            enable: false,
            type: StepType.GEN_OBS,
          },
          {
            id: "",
            text: "Sugestões",
            checked: true,
            enable: false,
            type: StepType.SUGGESTIONS,
          },
        ];

  const [step, setStep] = useState<IStep[]>(startCompostion);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [orderCompositionList, setOrderCompositionList] = useState<
    OrderComposition[]
  >([]);

  const [enableNextButton, setenableNextButton] = useState(false);
  const [showObsModal, setShowObsModal] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [hidghlightAll, setHidghlightAll] = useState(false);

  const toggleVisibility = useCallback(
    (index: number) => {
      if (enableNextButton) {
        setCurrentStep(index);
      } else {
        setIsHighlighted(true);
      }
    },
    [enableNextButton]
  );

  useEffect(() => {
    if (isHighlighted) {
      const timeout = setTimeout(() => setIsHighlighted(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isHighlighted]);

  useEffect(() => {
    if (hidghlightAll) {
      const timeout = setTimeout(() => setHidghlightAll(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [hidghlightAll]);

  const getCurrentQuantity = useCallback(
    (compostionId: string) => {
      const composition = orderCompositionList.find(
        (it) => it.orderCompositionItems[0]?.compositionId === compostionId
      );
      if (composition) {
        const qtd = composition.orderCompositionItems.reduce(
          (acc, cur) => (acc += cur.quantity),
          0
        );
        return qtd;
      } else {
        return 0;
      }
    },
    [orderCompositionList]
  );

  const getFreeMax = useCallback(
    (compostionId: string) => {
      const composition = orderCompositionList.find(
        (it) => it.orderCompositionItems[0]?.compositionId === compostionId
      );
      if (composition) {
        const qtd = composition.orderCompositionItems.reduce(
          (acc, cur) => (acc += cur.quantityFree),
          0
        );
        return qtd;
      } else {
        return 0;
      }
    },
    [orderCompositionList]
  );

  const getCurrentFree = useCallback(
    (compostionId: string) => {
      const composition = orderCompositionList.find(
        (it) => it.orderCompositionItems[0]?.compositionId === compostionId
      );
      if (composition) {
        const qtd = composition.orderCompositionItems.reduce(
          (acc, cur) => (acc += cur.quantityFreeSelected),
          0
        );
        return qtd;
      } else {
        return 0;
      }
    },
    [orderCompositionList]
  );

  const onClickContinue = useCallback(() => {
    if (!enableNextButton) {
      setIsHighlighted(true);
      return;
    }
    if (currentStep < step.length - 1 && compositions[currentStep]?.min === 0) {
      setStep((prev) => {
        let newValue = prev;
        newValue[currentStep].checked = true;
        return newValue;
      });
    }
    if (currentStep < step.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
    if (currentStep === step.length - 1) {
      if (step.every((item) => item.checked)) {
        addToCard();
      } else {
        setHidghlightAll(true);
      }
    }
  }, [addToCard, compositions, enableNextButton, currentStep, step]);

  const onChangeComposition = useCallback(
    (
      newOrderComposition: OrderComposition,
      minimo: boolean,
      maximo: boolean
    ) => {
      setStep((prev) => {
        let newValue = prev.map((item, _index) =>
          item.id === newOrderComposition.compositionId
            ? { ...item, checked: minimo }
            : item
        );
        return newValue;
      });

      setenableNextButton(minimo);

      if (orderCompositionList.length <= 0) {
        let _orderCompositionList = [
          ...orderCompositionList,
          newOrderComposition,
        ];
        setOrderCompositionList(_orderCompositionList);

        // console.log("COMPOSICAO ZERADO");
      } else {
        let _orderCompositionList = [...orderCompositionList];
        let index = _orderCompositionList.findIndex(
          (_orderCompositionListItem) => {
            return (
              _orderCompositionListItem.compositionId ===
              newOrderComposition.compositionId
            );
          }
        );
        if (index === -1) {
          _orderCompositionList.push(newOrderComposition);
          setOrderCompositionList(_orderCompositionList);
          // console.log("COMPOSICAO COM VALOR ADD +1");
        } else {
          _orderCompositionList[index] = newOrderComposition;
          setOrderCompositionList(_orderCompositionList);
          // console.log("COMPOSICAO COM VALOR SUBSTITUI");
        }
      }

      // console.log("orderCompositionList : ", orderCompositionList);
    },
    [orderCompositionList]
  );

  useEffect(() => {
    if (
      step[currentStep].type === StepType.GEN_OBS ||
      step[currentStep].type === StepType.SUGGESTIONS
    ) {
      setenableNextButton(true);
    }
    if (step.every((item) => item.checked) && currentStep === step.length - 1) {
      onFinish(orderCompositionList, true);
    } else {
      onFinish([], false);
    }
  }, [step, onFinish, currentStep, orderCompositionList]);

  const totalCompositionPrice = useMemo(
    () => orderCompositionList.reduce((acc, cur) => (acc += cur.totalPrice), 0),
    [orderCompositionList]
  );

  const onpressKeyboard = useCallback(
    (key: string) => {
      handleChangeObservation(observation + key);
    },
    [handleChangeObservation, observation]
  );

  const onpressback = useCallback(() => {
    handleChangeObservation(observation.substring(0, observation.length - 1));
  }, [handleChangeObservation, observation]);

  const hasComposition = useCallback((product?: Product) => {
    if (product && product.compositions?.length) {
      if (product.compositions.some((it) => it.compositionItems.length > 0)) {
        return true;
      } else if (
        product?.cartRecommendationByProduct.length &&
        product.cartRecommendationByProduct.length > 0
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, []);

  const addSuggestionToCart = useCallback(
    (suggestProduct: Product) => {
      if (
        hasComposition(suggestProduct) ||
        suggestProduct.productGeneralObservation
      ) {
        if (step.every((item) => item.checked)) {
          addToCard();
          setTimeout(() => {
            openProductDetail(suggestProduct.id);
          }, 500);
        } else {
          setHidghlightAll(true);
        }
      } else {
        addProductCart(suggestProduct, 1, [], suggestProduct.adultHood);
      }
    },
    [addProductCart, addToCard, hasComposition, openProductDetail, step]
  );

  const handleRemoveSuggestionItem = useCallback(
    (event: React.MouseEvent<HTMLElement>, id: string) => {
      event.stopPropagation();
      removeCartItemByProductId(id, 1);
    },
    [removeCartItemByProductId]
  );

  const onImgError = (ev: React.ChangeEvent<HTMLImageElement>) => {
    ev.target.src = require("../../../../assets/images/no-image.png");
  };

  const calcMaxHeight = useCallback(() => {
    if (orientation === OrientationType.LANDSCAPE) {
      if (resolution === ResolutionType.FULL_HD) {
        return `calc(100 * var(--vh, 1vh) - 550px)`;
      } else {
        return `calc(100 * var(--vh, 1vh) - 565px)`;
      }
    } else {
      if (resolution === ResolutionType.FULL_HD) {
        return `calc(100 * var(--vh, 1vh) - 150px)`;
      } else {
        return `calc(100 * var(--vh, 1vh) - 230px)`;
      }
    }
  }, [orientation, resolution]);

  return (
    <div
      id={styles.CompositionList}
      style={{
        maxHeight: calcMaxHeight(),
        minHeight: calcMaxHeight(),
      }}
    >
      <div>
        {compositions.map((composicao, index) => {
          return (
            <div
              className={`${styles.item} ${
                currentStep === index ? styles.open : ""
              }`}
              key={index}
              style={{
                marginTop: resolution === ResolutionType.FULL_HD ? 12 : 8,
              }}
            >
              <p
                onClick={() => toggleVisibility(index)}
                style={{
                  marginBottom: resolution === ResolutionType.FULL_HD ? 12 : 6,
                }}
              >
                <span
                  style={{
                    color: currentStep === index ? "var(--primary)" : "#BDBDBD",
                    fontSize: resolution === ResolutionType.FULL_HD ? 32 : 26,
                  }}
                >
                  {composicao.description}
                </span>
                {composicao.min > 0 &&
                  composicao.min > getCurrentQuantity(composicao.id) && (
                    <span
                      className={`${styles.min} ${
                        isHighlighted && currentStep === index
                          ? styles.highlight
                          : hidghlightAll && currentStep !== index
                          ? styles.highlight
                          : ""
                      }`}
                      style={{
                        fontSize:
                          resolution === ResolutionType.FULL_HD ? 24 : 18,
                      }}
                    >
                      {t("cart_view.suggestions.mandatory")}
                    </span>
                  )}
                {getFreeMax(composicao.id) > getCurrentFree(composicao.id) && (
                  <span
                    className={styles.free}
                    style={{
                      fontSize: resolution === ResolutionType.FULL_HD ? 24 : 18,
                    }}
                  >
                    {t("cart_view.suggestions.free", {
                      max_free:
                        getFreeMax(composicao.id) -
                        getCurrentFree(composicao.id),
                    })}
                  </span>
                )}
                {currentStep === index ? (
                  <KeyboardArrowUp />
                ) : (
                  <KeyboardArrowDown />
                )}
              </p>
              <div
                className={styles.content}
                style={{
                  maxHeight:
                    resolution === ResolutionType.FULL_HD ? "800px" : "500px",
                }}
              >
                {currentStep === index && (
                  <Composition
                    compostionFreeMax={compostionFreeMax}
                    composition={composicao}
                    onChange={onChangeComposition}
                    initalValues={orderCompositionList.find(
                      (_ordercomposition) =>
                        _ordercomposition.compositionId === composicao.id
                    )}
                    setSelectedItemsFree={setSelectedItemsFree}
                    selectedItemsFree={selectedItemsFree}
                    getTotalItemsFree={getTotalItemsFree}
                    selectedItemsReplica={selectedItemsReplica}
                    setSelectedItemsReplica={setSelectedItemsReplica}
                  />
                )}
              </div>
            </div>
          );
        })}
        {generalObservation && (
          <div
            className={`${styles.item} ${
              step[currentStep].type === StepType.GEN_OBS ? styles.open : ""
            }`}
            style={{
              marginTop: resolution === ResolutionType.FULL_HD ? 12 : 8,
            }}
          >
            <p
              onClick={() =>
                toggleVisibility(
                  step.length - (suggestionProducts.length > 0 ? 2 : 1)
                )
              }
            >
              <span
                style={{
                  color:
                    step[currentStep].type === StepType.GEN_OBS
                      ? "var(--primary)"
                      : "#BDBDBD",
                  fontSize: resolution === ResolutionType.FULL_HD ? 32 : 26,
                }}
              >
                Observações
              </span>
              {step[currentStep].type === StepType.GEN_OBS ? (
                <KeyboardArrowUp />
              ) : (
                <KeyboardArrowDown />
              )}
            </p>
            <div className={styles.content}>
              {step[currentStep].type === StepType.GEN_OBS && (
                <div className={styles.obsInput}>
                  <p
                    onClick={() => setShowObsModal(true)}
                    className={styles.input}
                  >
                    {observation.length > 0
                      ? observation
                      : t("cart_view.suggestions.observation")}
                  </p>
                  <BottomModal
                    showModal={showObsModal}
                    onClickClose={() => setShowObsModal(false)}
                  >
                    <div className={styles.obsInput}>
                      <span>Observações</span>
                      <p className={styles.input}>
                        {observation.length > 0
                          ? observation
                          : t("cart_view.suggestions.write_observation")}
                      </p>
                      <Keyboard
                        onPressBack={onpressback}
                        onKeyPress={onpressKeyboard}
                        onPressEnter={() => setShowObsModal(false)}
                      />
                    </div>
                  </BottomModal>
                </div>
              )}
            </div>
          </div>
        )}
        {suggestionProducts.length > 0 && (
          <div
            className={`${styles.item} ${
              step[currentStep].type === StepType.SUGGESTIONS ? styles.open : ""
            }`}
            style={{
              marginTop: resolution === ResolutionType.FULL_HD ? 12 : 8,
            }}
          >
            <p onClick={() => toggleVisibility(step.length - 1)}>
              <span
                style={{
                  color:
                    step[currentStep].type === StepType.SUGGESTIONS
                      ? "var(--primary)"
                      : "#BDBDBD",
                  fontSize: resolution === ResolutionType.FULL_HD ? 32 : 26,
                }}
              >
                <Crown />
                {t("cart_view.suggestions.also_enjoy")}
              </span>
              {step[currentStep].type === StepType.SUGGESTIONS ? (
                <KeyboardArrowUp />
              ) : (
                <KeyboardArrowDown />
              )}
            </p>
            <div className={styles.content}>
              {step[currentStep].type === StepType.SUGGESTIONS && (
                <div className={styles.suggestionContainer}>
                  {suggestionProducts.map((it, key) => (
                    <div className={styles.quantity} key={key}>
                      <div
                        className={styles.product}
                        onClick={() => addSuggestionToCart(it)}
                      >
                        <img
                          src={it.thumbnailUrl}
                          onErrorCapture={onImgError}
                          alt="produto"
                          style={{
                            width:
                              resolution === ResolutionType.FULL_HD ? 75 : 60,
                            height:
                              resolution === ResolutionType.FULL_HD ? 75 : 60,
                          }}
                        />
                        <div
                          style={{
                            minHeight:
                              resolution === ResolutionType.FULL_HD ? 110 : 75,
                          }}
                        >
                          <span
                            style={{
                              fontSize:
                                resolution === ResolutionType.FULL_HD ? 30 : 20,
                              marginBottom:
                                resolution === ResolutionType.FULL_HD ? 8 : 6,
                            }}
                          >
                            {it.name}
                          </span>
                          <span
                            style={{
                              fontSize:
                                resolution === ResolutionType.FULL_HD ? 24 : 16,
                            }}
                          >
                            {moneyMask(it.price)}
                          </span>
                        </div>
                      </div>
                      <div
                        className={styles.quantitySelector}
                        style={{
                          height:
                            resolution === ResolutionType.FULL_HD ? 60 : 50,
                        }}
                      >
                        <div
                          style={
                            {
                              width:
                                resolution === ResolutionType.FULL_HD
                                  ? 200
                                  : 160,
                              "--color": hexToRGBA(
                                kiosk?.themeColor ??
                                  kiosk?.primaryColor ??
                                  "#001537",
                                0.1
                              ),
                            } as CSSProperties
                          }
                        >
                          <button
                            onClick={(ev) =>
                              handleRemoveSuggestionItem(ev, it.id)
                            }
                          >
                            <Icon>remove</Icon>
                          </button>
                          <span>{getProductQuantity(it.id)}</span>
                          <button onClick={() => addSuggestionToCart(it)}>
                            <Icon>add</Icon>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div
        className={styles.footer}
        style={{
          margin: resolution === ResolutionType.FULL_HD ? 48 : 36,
        }}
      >
        <div onClick={onClickContinue}>
          <span>{`R$ ${(productBaseValue + totalCompositionPrice)
            .toFixed(2)
            .replace(".", ",")}`}</span>
          <span>{t("cart_view.suggestions.continue")}</span>
        </div>
      </div>
    </div>
  );
};
